import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';
import { Icon } from '~/components';
import { colors, weights } from '~/styles';
import AssistantChart from '~/routes/app/ai/assistant/Chart';
import AssistantSchedule from '~/routes/app/ai/assistant/Schedule';
import AssistantTable from '~/routes/app/ai/assistant/Table';

const ErrorBlock = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${({ $variant }) => ($variant === 'warning' ? colors.warning10 : colors.danger5)};
  border: 1px solid ${({ $variant }) => ($variant === 'warning' ? colors.warning25 : colors.danger25)};
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  color: ${({ $variant }) => ($variant === 'warning' ? colors.warning100 : colors.danger)};
  font-size: 0.875rem;
  line-height: 1.5;
`;

const ErrorIcon = styled.span`
  flex-shrink: 0;
`;

const ScheduleWrapper = styled.div`
  border: solid 1px ${colors.grey10};
  border-radius: 0.25rem;
  overflow: hidden;
  background: ${colors.white};

  > div {
    margin: 0;
  }
`;

const PartsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const TableWrapper = styled.div`
  background: ${colors.white};
  border: solid 1px ${colors.grey10};
  border-radius: 0.25rem;
  overflow: hidden;

  > div {
    margin: 0;
  }
`;

const MarkdownWrapper = styled.div`
  > *:last-child {
    margin-bottom: 0;
  }

  code {
    font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Fira Mono', monospace;
    font-size: 0.8125rem;
    background: ${colors.grey5};
    padding: 0.125rem 0.25rem;
    border-radius: 0.125rem;
  }

  pre {
    background: ${colors.grey5};
    border: solid 1px ${colors.grey10};
    border-radius: 0.25rem;
    padding: 0.75rem;
    overflow-x: auto;
    margin-bottom: 0.5rem;

    code {
      background: none;
      padding: 0;
    }
  }

  table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 0.75rem;
    font-size: 0.8125rem;
    background: ${colors.white};
    border-radius: 0.25rem;
    overflow: hidden;
  }

  th,
  td {
    border: solid 1px ${colors.grey10};
    padding: 0.375rem 0.5rem;
    text-align: left;
  }

  th {
    background: ${colors.white};
    font-weight: ${weights.bold};
  }
`;

const ToolBlock = styled.div`
  border: 1px solid ${colors.grey10};
  border-radius: 0.5rem;
  display: inline-block;
`;

const ToolHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem;
  cursor: pointer;

  &:hover {
    background: ${colors.grey5};
  }
`;

const ToolStatusIcon = styled(Icon)`
  font-size: 0.75rem;
  color: ${({ $failed }) => ($failed ? colors.danger : colors.success)};
`;

const ToolHeaderChevron = styled.span`
  font-size: 0.625rem;
  color: ${colors.grey40};
  width: 0.75rem;
`;

const ToolHeaderText = styled.span`
  font-size: 0.8125rem;
  color: ${colors.grey55};
`;

const ToolBody = styled.div`
  position: relative;
  padding: 0 0.75rem 0.75rem 0.75rem;
`;

const ToolConnectingLine = styled.div`
  position: absolute;
  left: 1rem;
  top: 0;
  bottom: 1.5rem;
  width: 1px;
  background: ${colors.grey5};
`;

const ToolStep = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.25rem 0;
`;

const ToolStepIcon = styled.span`
  width: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${colors.white};
  font-size: 0.625rem;

  &.completed {
    color: ${colors.success};
  }
  &.failed {
    color: ${colors.danger};
  }
`;

const ToolStepText = styled.span`
  font-size: 0.8125rem;
  color: ${colors.grey55};
  line-height: 1.4;
`;

function ToolsPart({ tools }) {
  const [expanded, setExpanded] = React.useState(true);

  const completedCount = tools.filter((t) => t.status === 'completed').length;
  const failedCount = tools.filter((t) => t.status === 'failed').length;

  let headerText = `${completedCount} step${completedCount !== 1 ? 's' : ''} completed`;
  if (failedCount > 0) headerText += `, ${failedCount} failed`;

  if (tools.length === 0) return null;

  return (
    <ToolBlock>
      <ToolHeader onClick={() => setExpanded(!expanded)}>
        <ToolHeaderChevron>
          <Icon icon={expanded ? 'chevron-down' : 'chevron-right'} />
        </ToolHeaderChevron>
        <ToolStatusIcon icon={failedCount > 0 ? 'circle-exclamation' : 'circle-check'} $failed={failedCount > 0} />
        <ToolHeaderText>{headerText}</ToolHeaderText>
      </ToolHeader>

      {expanded && (
        <ToolBody>
          <ToolConnectingLine />
          {tools.map((tool) => (
            <ToolStep key={tool.callId}>
              <ToolStepIcon className={tool.status}>
                <Icon icon={tool.status === 'completed' ? 'check' : 'xmark'} />
              </ToolStepIcon>
              <ToolStepText>{tool.statusText}</ToolStepText>
            </ToolStep>
          ))}
        </ToolBody>
      )}
    </ToolBlock>
  );
}

function TextPart({ content }) {
  return (
    <MarkdownWrapper>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
    </MarkdownWrapper>
  );
}

function ChartPart({ chartType, data, fields, dimension, metric, series, colors: colorMap, filename }) {
  return (
    <div>
      <AssistantChart
        chartType={chartType}
        data={data}
        fields={fields}
        dimension={dimension}
        metric={metric}
        series={series}
        colors={colorMap}
        filename={filename}
      />
    </div>
  );
}

export default function MessageContent({ parts }) {
  if (!parts || parts.length === 0) return null;

  const rendered = parts.map((part, i) => {
    if (part.type === 'tools') {
      return <ToolsPart key={`tools-${i}`} tools={part.tools || []} />;
    }

    if (part.type === 'text') {
      return <TextPart key={`text-${i}`} content={part.content || ''} />;
    }

    if (part.type === 'table') {
      return (
        <TableWrapper key={`table-${i}`}>
          <AssistantTable
            data={part.data}
            fields={part.fields}
            meta={part.meta}
            pagination={part.pagination}
            filename={part.filename}
          />
        </TableWrapper>
      );
    }

    if (part.type === 'schedule') {
      return (
        <ScheduleWrapper key={`schedule-${i}`}>
          <AssistantSchedule
            data={part.data}
            fields={part.fields}
            meta={part.meta}
            pagination={part.pagination}
            filename={part.filename}
          />
        </ScheduleWrapper>
      );
    }

    if (part.type === 'error') {
      const variant = part.code === 'AI_BUDGET_LIMIT' ? 'warning' : 'danger';
      const iconColor = variant === 'warning' ? colors.warning100 : colors.danger;
      return (
        <ErrorBlock key={`error-${i}`} $variant={variant}>
          <ErrorIcon>
            <Icon icon="exclamation-triangle" color={iconColor} />
          </ErrorIcon>
          {part.message}
        </ErrorBlock>
      );
    }

    if (part.type === 'chart') {
      return (
        <ChartPart
          key={`chart-${i}`}
          chartType={part.chartType}
          data={part.data}
          fields={part.fields}
          dimension={part.dimension}
          metric={part.metric}
          series={part.series}
          colors={part.colors}
          filename={part.filename}
        />
      );
    }

    return null;
  });

  return <PartsWrapper>{rendered}</PartsWrapper>;
}
